// biome-ignore-all lint/security/noDangerouslySetInnerHtml: Page bodies are sanitized by the public read API before storage.
import { type ReactNode, useEffect, useRef } from 'react';
import type { GraphNode, InfoboxItem, PageRead, TranslationLink, WikiLink } from '@/api/client';
import { defaultLang } from '@/app/routes';
import { SITE } from '@/config';
import { locales } from '@/i18n';
import { useT } from '@/i18n/LocaleProvider';
import { slugify } from '@/lib/slug';

export type ReaderArticleProps = {
  backlinks?: GraphNode[];
  error?: string | null;
  fallbackCoverImage: string;
  homeSlug?: string;
  isLoading?: boolean;
  mapPreviewSlot?: ReactNode;
  watchSlot?: ReactNode;
  outboundIds?: string[];
  page?: PageRead | null;
  pageMissingSlug?: string;
  relevant?: GraphNode[];
  showCover?: boolean;
  siteTitle?: string;
  currentLang?: string;
  activeSlug?: string;
  onCreateMissingPage?: () => void;
  onEdit?: (page: PageRead) => void;
  onHistory?: (page: PageRead) => void;
  onInfo?: (page: PageRead) => void;
  onNavigate?: (slug: string) => void;
  onNavigateCategory?: (name: string) => void;
  onNavigateNoRedirect?: (slug: string) => void;
  onNavigateLang?: (lang: string, slug: string) => void;
  onProposeMissing?: (defaults: MissingProposalDefaults) => void;
};

const publishedDateFormatter = new Intl.DateTimeFormat('ru');
const wikiReferencePattern = /\[\[([^\]|#]+)(?:#[^\]|]+)?(?:\|([^\]]+))?\]\]/g;

export type MissingProposalDefaults = {
  slug?: string;
  title?: string;
  language?: string;
  translationGroup?: string;
};

export function ReaderArticle({
  backlinks = [],
  error,
  fallbackCoverImage,
  homeSlug,
  isLoading = false,
  mapPreviewSlot,
  watchSlot,
  outboundIds = [],
  page,
  pageMissingSlug,
  relevant = [],
  showCover,
  siteTitle = SITE.title,
  currentLang,
  activeSlug,
  onCreateMissingPage,
  onEdit,
  onHistory,
  onInfo,
  onNavigate,
  onNavigateCategory,
  onNavigateNoRedirect,
  onNavigateLang,
  onProposeMissing,
}: ReaderArticleProps) {
  const t = useT();
  const noteBodyRef = useRef<HTMLDivElement>(null);
  const cover = page?.infobox.cover;
  const coverTitle = cover?.title || page?.title || siteTitle;
  const coverKicker = cover?.kicker || (page ? pageStatus(page, t) : '');
  const coverImage = cover?.image || page?.media_urls[0] || fallbackCoverImage;
  const categories = page?.categories ?? [];
  const firstCategory = categories[0];
  const shouldShowCover = showCover ?? page?.slug === homeSlug;
  const publishedAt = page?.published_at ? formatPublishedDate(page.published_at) : null;

  useEffect(() => {
    const noteBody = noteBodyRef.current;

    if (!noteBody || (!onNavigate && !onProposeMissing)) {
      return;
    }

    const handleClick = (event: globalThis.MouseEvent) => {
      handleArticleClick(event, onNavigate, onProposeMissing);
    };

    noteBody.addEventListener('click', handleClick);

    return () => noteBody.removeEventListener('click', handleClick);
  }, [onNavigate, onProposeMissing]);

  return (
    <>
      {shouldShowCover ? (
        <section className="cover" aria-label={coverTitle}>
          <img id="cover-image" src={coverImage} alt="" />
          <div className="cover-text">
            <p>{coverKicker}</p>
            <h1>{coverTitle}</h1>
          </div>
        </section>
      ) : null}
      {shouldShowCover ? mapPreviewSlot : null}

      <article className="note" data-testid="reader-article" id="note" tabIndex={-1}>
        {isLoading ? <div className="empty-note">{t('reader_page_loading')}</div> : null}
        {!isLoading && error ? <div className="empty-note">{error}</div> : null}
        {!isLoading && !error && !page ? (
          <MissingPageState
            activeSlug={activeSlug}
            currentLang={currentLang}
            pageMissingSlug={pageMissingSlug}
            onProposeMissing={onProposeMissing}
            onCreateMissingPage={onCreateMissingPage}
          />
        ) : null}
        {!isLoading && !error && page ? (
          <>
            {page.redirected_from ? (
              <div className="redirect-banner" role="note">
                {t('reader_redirected_from')}{' '}
                <a
                  href={`${pageHref(currentLang, page.redirected_from)}?redirect=no`}
                  onClick={(event) => {
                    event.preventDefault();
                    const from = page.redirected_from;
                    if (from) onNavigateNoRedirect?.(from);
                  }}
                >
                  <strong>{page.redirected_from}</strong>
                </a>
              </div>
            ) : null}
            {page.redirect_loop ? (
              <div className="redirect-banner is-loop" role="alert">
                {t('reader_redirect_loop')}
              </div>
            ) : null}

            <nav className="breadcrumbs" aria-label={t('reader_breadcrumbs_label')}>
              <button
                type="button"
                onClick={() => {
                  if (homeSlug) {
                    onNavigate?.(homeSlug);
                  }
                }}
              >
                {t('reader_home')}
              </button>
              <span aria-hidden="true">›</span>
              {firstCategory ? (
                <>
                  <button type="button" onClick={() => onNavigateCategory?.(firstCategory)}>
                    {firstCategory}
                  </button>
                  <span aria-hidden="true">›</span>
                </>
              ) : null}
              <span aria-current="page">{page.title}</span>
            </nav>

            <ul className="article-tabs">
              <li>
                <button className="is-active" type="button" aria-current="page">
                  {t('reader_read')}
                </button>
              </li>
              {onEdit ? (
                <li>
                  <button type="button" onClick={() => onEdit(page)}>
                    {t('reader_edit')}
                  </button>
                </li>
              ) : null}
              <li>
                <button type="button" onClick={() => onHistory?.(page)}>
                  {t('reader_history')}
                </button>
              </li>
              <li className="tab-right">
                <button type="button" onClick={() => onInfo?.(page)}>
                  {t('reader_info')}
                </button>
              </li>
              {watchSlot ? <li className="tab-right">{watchSlot}</li> : null}
            </ul>

            <header className="note-header">
              <h1>{page.title}</h1>
            </header>

            {page.infobox.items.length > 0 ? (
              <FactGrid
                items={page.infobox.items}
                links={page.links}
                lang={currentLang}
                onNavigate={onNavigate}
                onProposeMissing={onProposeMissing}
              />
            ) : null}

            {page.html ? (
              <div
                className="note-body"
                ref={noteBodyRef}
                dangerouslySetInnerHTML={{ __html: page.html }}
              />
            ) : (
              <div className="empty-note">{t('reader_page_empty')}</div>
            )}

            {categories.length > 0 ? (
              <footer className="page-categories">
                <span>{t('reader_categories_label')}</span>
                {categories.map((category) => (
                  <a
                    href={categoryHref(category)}
                    key={category}
                    onClick={(event) => {
                      if (!onNavigateCategory) {
                        return;
                      }

                      event.preventDefault();
                      onNavigateCategory(category);
                    }}
                  >
                    {category}
                  </a>
                ))}
              </footer>
            ) : null}

            <RelevantLinksTable
              backlinks={backlinks}
              outboundIds={outboundIds}
              relevant={relevant}
              lang={currentLang}
              onNavigate={onNavigate}
            />

            {page.translations && page.translations.length > 0 ? (
              <TranslationLinks
                currentLang={page.language}
                translations={page.translations}
                onNavigateLang={onNavigateLang}
              />
            ) : null}

            <div className="article-tools">
              {publishedAt ? (
                <span>
                  {t('reader_published_prefix')} {publishedAt}
                </span>
              ) : null}
              {page.author ? (
                <span title={page.author.email ?? undefined}>{page.author.name}</span>
              ) : null}
            </div>
          </>
        ) : null}
      </article>
    </>
  );
}

type RelevantLinksTableProps = {
  backlinks: GraphNode[];
  outboundIds: string[];
  relevant: GraphNode[];
  lang?: string;
  onNavigate?: (slug: string) => void;
};

function RelevantLinksTable({
  backlinks,
  outboundIds,
  relevant,
  lang,
  onNavigate,
}: RelevantLinksTableProps) {
  const t = useT();

  if (relevant.length === 0) {
    return null;
  }

  const inboundSet = new Set(backlinks.map((n) => n.id));
  const outboundSet = new Set(outboundIds);

  return (
    <section className="rel-links-section" aria-label={t('reader_relevant_label')}>
      <h2>{t('reader_relevant_label')}</h2>
      <table className="rel-links">
        <thead>
          <tr>
            <th>{t('reader_relevant_page')}</th>
            <th>{t('reader_relevant_link')}</th>
          </tr>
        </thead>
        <tbody>
          {relevant.map((node) => {
            const isMutual = inboundSet.has(node.id) && outboundSet.has(node.id);
            const isInbound = inboundSet.has(node.id);
            const marker = isMutual ? '↔' : isInbound ? '←' : '→';
            const markerLabel = isMutual
              ? t('reader_relevant_mutual')
              : isInbound
                ? t('reader_relevant_inbound')
                : t('reader_relevant_outbound');
            const slug = node.slug;

            return (
              <tr key={node.id}>
                <td>
                  {slug ? (
                    <a
                      href={pageHref(lang, slug)}
                      onClick={(event) => {
                        event.preventDefault();
                        onNavigate?.(slug);
                      }}
                    >
                      {node.title}
                    </a>
                  ) : (
                    <span>{node.title}</span>
                  )}
                </td>
                <td aria-label={markerLabel}>{marker}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

type FactGridProps = {
  items: InfoboxItem[];
  links: WikiLink[];
  lang?: string;
  onNavigate?: (slug: string) => void;
  onProposeMissing?: (defaults: MissingProposalDefaults) => void;
};

function FactGrid({ items, links, lang, onNavigate, onProposeMissing }: FactGridProps) {
  return (
    <dl className="fact-grid" data-testid="fact-grid">
      {items.map((item) => (
        <div className="fact-row" key={item.key}>
          <dt>{item.key}</dt>
          <dd>{renderInfoboxValue(item.value, links, onNavigate, onProposeMissing, lang)}</dd>
        </div>
      ))}
    </dl>
  );
}

export function renderInfoboxValue(
  value: string,
  links: WikiLink[],
  onNavigate?: (slug: string) => void,
  onProposeMissing?: (defaults: MissingProposalDefaults) => void,
  lang?: string,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  for (const match of value.matchAll(wikiReferencePattern)) {
    const raw = match[0] ?? '';
    const target = match[1] ?? '';
    const label = match[2];
    const index = match.index ?? 0;

    if (!raw || !target) {
      continue;
    }

    if (index > cursor) {
      nodes.push(value.slice(cursor, index));
    }

    const displayLabel = label?.trim() || displayTarget(target);
    const resolvedLink = resolveWikiLink(target, links);

    if (resolvedLink?.resolved && resolvedLink.slug) {
      const slug = resolvedLink.slug;

      nodes.push(
        <a
          className="wiki-link"
          href={pageHref(lang, slug)}
          key={`${target}-${index}`}
          onClick={(event) => {
            event.preventDefault();
            onNavigate?.(slug);
          }}
        >
          {displayLabel}
        </a>,
      );
    } else {
      const defaults = missingDefaults(target, displayLabel);

      nodes.push(
        <a
          className="missing-link"
          href={proposalHref(defaults)}
          key={`${target}-${index}`}
          onClick={(event) => {
            event.preventDefault();
            onProposeMissing?.(defaults);
          }}
        >
          {displayLabel}
        </a>,
      );
    }

    cursor = index + raw.length;
  }

  if (cursor < value.length) {
    nodes.push(value.slice(cursor));
  }

  return nodes.length > 0 ? nodes : [value];
}

export function slugFromWikiHref(href: string): string | null {
  const trimmed = href.trim();

  if (!trimmed) {
    return null;
  }

  const hashMatch = trimmed.match(/^#\/(.+)$/);
  if (hashMatch?.[1]) {
    return decodeSlug(hashMatch[1]);
  }

  const pathMatch = trimmed.match(/^\/pages\/([^?#]+)$/);
  if (pathMatch?.[1]) {
    return decodeSlug(pathMatch[1]);
  }

  const absolute = sameOriginPath(trimmed);
  if (absolute && absolute !== trimmed) {
    return slugFromWikiHref(absolute);
  }

  return null;
}

export function proposalDefaultsFromHref(href: string): MissingProposalDefaults | null {
  const trimmed = href.trim();

  if (!trimmed) {
    return null;
  }

  const path = sameOriginPath(trimmed) ?? trimmed;
  const url = new URL(path, window.location.origin);

  if (url.pathname !== '/propose') {
    return null;
  }

  const slug = url.searchParams.get('slug')?.trim() || undefined;
  const title = url.searchParams.get('title')?.trim() || undefined;

  if (!slug && !title) {
    return null;
  }

  return { slug, title };
}

function handleArticleClick(
  event: globalThis.MouseEvent,
  onNavigate?: (slug: string) => void,
  onProposeMissing?: (defaults: MissingProposalDefaults) => void,
) {
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) {
    return;
  }

  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  const anchor = target.closest('a');

  if (!anchor) {
    return;
  }

  const href = anchor.getAttribute('href') ?? '';
  const defaults = proposalDefaultsFromHref(href);

  if (defaults && onProposeMissing) {
    event.preventDefault();
    onProposeMissing(defaults);

    return;
  }

  const slug = slugFromWikiHref(href);

  if (!slug || !onNavigate) {
    return;
  }

  event.preventDefault();
  onNavigate(slug);
}

function resolveWikiLink(target: string, links: WikiLink[]): WikiLink | undefined {
  const normalizedTarget = normalizeLookup(target);

  return links.find((link) => {
    return (
      normalizeLookup(link.target) === normalizedTarget ||
      normalizeLookup(link.slug ?? '') === normalizedTarget
    );
  });
}

function proposalHref(defaults: MissingProposalDefaults): string {
  const params = new URLSearchParams();

  if (defaults.slug) {
    params.set('slug', defaults.slug);
  }

  if (defaults.title) {
    params.set('title', defaults.title);
  }

  const query = params.toString();

  return query ? `/propose?${query}` : '/propose';
}

// pageHref builds a language-prefixed page URL (the only canonical content URL form,
// ADR-0003). Slugs are native-script titles, kept verbatim via encodeURIComponent.
function pageHref(lang: string | undefined, slug: string): string {
  return `/${lang ?? defaultLang}/pages/${encodeURIComponent(slug)}`;
}

function categoryHref(category: string): string {
  return `/categories/${encodeURIComponent(category)}`;
}

function formatPublishedDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return publishedDateFormatter.format(date);
}

function missingDefaults(target: string, title: string): MissingProposalDefaults {
  const cleanTarget = displayTarget(target);
  const cleanTitle = title.trim() || cleanTarget;

  return {
    slug: slugify(cleanTarget || cleanTitle),
    title: cleanTitle,
  };
}

function displayTarget(target: string): string {
  const clean = target.split('|')[0].split('#')[0].trim().replace(/\\/g, '/').replace(/\.md$/i, '');
  const parts = clean.split('/').filter(Boolean);

  return parts[parts.length - 1] || clean;
}

function normalizeLookup(value: string): string {
  return displayTarget(value).toLocaleLowerCase('ru');
}

function decodeSlug(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function sameOriginPath(href: string): string | null {
  try {
    const url = new URL(href, window.location.origin);

    if (url.origin !== window.location.origin) {
      return null;
    }

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function pageStatus(
  page: PageRead,
  t: (key: Parameters<ReturnType<typeof useT>>[0]) => string,
): string {
  if (page.is_stub) {
    return t('reader_page_stub');
  }

  return '';
}

type MissingPageStateProps = {
  currentLang?: string;
  activeSlug?: string;
  pageMissingSlug?: string;
  onProposeMissing?: (defaults: MissingProposalDefaults) => void;
  onCreateMissingPage?: () => void;
};

function MissingPageState({
  currentLang,
  activeSlug,
  pageMissingSlug,
  onProposeMissing,
  onCreateMissingPage,
}: MissingPageStateProps) {
  const t = useT();

  if (pageMissingSlug) {
    return (
      <div className="empty-note" data-testid="missing-page-cta">
        <p>{t('reader_page_missing', { slug: pageMissingSlug })}</p>
        <button type="button" onClick={onCreateMissingPage}>
          {t('reader_propose_missing')}
        </button>
      </div>
    );
  }

  if (currentLang && onProposeMissing) {
    const defaults: MissingProposalDefaults = { slug: activeSlug, language: currentLang };

    return (
      <div className="empty-note">
        <p>{t('reader_missing_lang', { lang: langLabel(currentLang) })}</p>
        <button type="button" onClick={() => onProposeMissing(defaults)}>
          {t('reader_propose_translation')}
        </button>
      </div>
    );
  }

  return <div className="empty-note">{t('reader_no_pages')}</div>;
}

type TranslationLinksProps = {
  currentLang: string;
  translations: TranslationLink[];
  onNavigateLang?: (lang: string, slug: string) => void;
};

function TranslationLinks({ currentLang, translations, onNavigateLang }: TranslationLinksProps) {
  const t = useT();
  const others = translations.filter((tr) => tr.language !== currentLang);

  if (others.length === 0) {
    return null;
  }

  return (
    <nav className="language-links" aria-label={t('reader_other_langs_label')}>
      <span>{t('reader_other_langs')}</span>
      {others.map((tr) => (
        <a
          key={tr.language}
          href={`/${tr.language}/pages/${encodeURIComponent(tr.slug)}`}
          onClick={(event) => {
            event.preventDefault();
            onNavigateLang?.(tr.language, tr.slug);
          }}
        >
          {langLabel(tr.language)}
        </a>
      ))}
    </nav>
  );
}

function langLabel(lang: string): string {
  return locales.find((l) => l.code === lang)?.label ?? lang;
}
